export interface ClientTheme {
  clientId: string
  clientName: string
  clientSubtitle: string

  /** Drives the CSS color-scheme so native controls (date pickers, checkboxes,
   *  scrollbars) match the theme's brightness. */
  colorScheme: "light" | "dark"

  // Brand palette
  brandPrimary: string
  brandAccent: string
  brandTeal: string
  brandGreen: string
  brandGold: string
  /** Sticky-note amber for note highlights. Always warm-yellow regardless of
   *  brand palette (brandGold is slate in some themes), tuned per background. */
  noteAccent: string

  // KPI accent order
  kpiAccents: string[]

  /** Interest-kind identity colors: leasehold = ink blue (the contract),
   *  mineral = copper (the ore). Tuned per palette for contrast; deliberately
   *  distinct hue families from ACQ green / DIV red so the two color languages
   *  never collide. Used by kind tabs and kind badges. */
  kindAccents: { leasehold: string; mineral: string }

  /** Wells/units identity color: a deliberately neutral slate (wells are
   *  operational fixtures, not a position kind), paired with the Pickaxe icon.
   *  Hex (not rgba) so it can take alpha suffixes for washes. */
  wellsAccent: string

  // Surfaces
  bgPage: string
  bgSurface: string
  bgSurface2: string
  bgSidebar: string
  bgHeader: string

  // Borders
  borderColor: string
  borderSubtle: string

  // Text
  textPrimary: string
  textMuted: string
  textDim: string
  textOnBrand: string

  // Sidebar
  sidebarActiveBg: string
  sidebarActiveBorder: string
  sidebarActiveText: string
  sidebarInactiveText: string
}

// ─── EnergyQuest Acquisitions ────────────────────────────────────────────────
export const energyQuestTheme: ClientTheme = {
  clientId: "energyquest",
  clientName: "EnergyQuest",
  clientSubtitle: "Acquisitions",
  colorScheme: "light",

  brandPrimary: "#1A2D6B",
  brandAccent: "#E55B2B",
  brandTeal: "#00B4C5",
  brandGreen: "#4DB848",
  brandGold: "#F5A623",
  noteAccent: "#D97706",

  kpiAccents: ["#1A2D6B", "#E55B2B", "#00B4C5", "#4DB848"],
  kindAccents: { leasehold: "#2C4BB0", mineral: "#B4691E" },
  wellsAccent: "#64748B",

  bgPage: "#F4F6F9",
  bgSurface: "#FFFFFF",
  bgSurface2: "#F0F4F8",
  bgSidebar: "#FFFFFF",
  bgHeader: "#FFFFFF",

  borderColor: "#E1E7EF",
  borderSubtle: "#EDF1F7",

  textPrimary: "#0F172A",
  textMuted: "#64748B",
  textDim: "#94A3B8",
  textOnBrand: "#FFFFFF",

  sidebarActiveBg: "#EEF1FB",
  sidebarActiveBorder: "#E55B2B",
  sidebarActiveText: "#1A2D6B",
  sidebarInactiveText: "#64748B",
}

// ─── SOWLE (legacy dark-gold theme) ──────────────────────────────────────────
export const sowleTheme: ClientTheme = {
  clientId: "sowle",
  clientName: "SOWLE",
  clientSubtitle: "Minerals",
  colorScheme: "dark",

  brandPrimary: "#C9A84C",
  brandAccent: "#C9A84C",
  brandTeal: "#2DD4BF",
  brandGreen: "#4ADE80",
  brandGold: "#C9A84C",
  noteAccent: "#E2B94E",

  kpiAccents: ["#C9A84C", "#2DD4BF", "#60A5FA", "#A78BFA"],
  kindAccents: { leasehold: "#7CA9F0", mineral: "#E08A45" },
  wellsAccent: "#98A2B3",

  bgPage: "#0A0C0E",
  bgSurface: "#111417",
  bgSurface2: "#181C20",
  bgSidebar: "#0A0C0E",
  bgHeader: "#111417",

  borderColor: "#252A30",
  borderSubtle: "#1A1F24",

  textPrimary: "#E8E3D8",
  textMuted: "#6B7280",
  textDim: "#3D4350",
  textOnBrand: "#0A0C0E",

  sidebarActiveBg: "#181C20",
  sidebarActiveBorder: "#C9A84C",
  sidebarActiveText: "#E8E3D8",
  sidebarInactiveText: "#6B7280",
}

// ─── LandTrack (forest green light theme) ────────────────────────────────────
export const landtrackTheme: ClientTheme = {
  clientId: "landtrack",
  clientName: "LandTrack",
  clientSubtitle: "Minerals",
  colorScheme: "light",

  brandPrimary: "#2E7D4F",
  brandAccent: "#3B9B63",
  brandTeal: "#3B4A5A",
  brandGreen: "#2E7D4F",
  brandGold: "#6B7A8D",
  noteAccent: "#B7791F",

  kpiAccents: ["#2E7D4F", "#3B9B63", "#3B4A5A", "#6B7A8D"],
  kindAccents: { leasehold: "#33628C", mineral: "#A8651F" },
  wellsAccent: "#6B7A8D",

  bgPage: "#F8F7F4",
  bgSurface: "#FFFFFF",
  bgSurface2: "#F8F7F4",
  bgSidebar: "#FFFFFF",
  bgHeader: "#FFFFFF",

  borderColor: "#E2EDE8",
  borderSubtle: "#EBF4EF",

  textPrimary: "#1A2332",
  textMuted: "#4A5568",
  textDim: "#718096",
  textOnBrand: "#FFFFFF",

  sidebarActiveBg: "#E8F5EE",
  sidebarActiveBorder: "#2E7D4F",
  sidebarActiveText: "#1A2332",
  sidebarInactiveText: "#4A5568",
}

// ─── LandTrack dark (slate-night counterpart to the light theme) ─────────────
// Surface/border/text values come verbatim from the approved "Light Forward"
// design file's dark palette; brand values carry over from the handoff spec.
export const landtrackDarkTheme: ClientTheme = {
  clientId: "landtrack-dark",
  clientName: "LandTrack",
  clientSubtitle: "Minerals",
  colorScheme: "dark",

  // #2E7D4F (not the lighter #3B9B63) — matches the DC dark palette's
  // toggle/active accents and keeps white-on-primary text at ≥4.5:1.
  brandPrimary: "#2E7D4F",
  brandAccent: "#4FB878",
  brandTeal: "#8FA3B8",
  brandGreen: "#3B9B63",
  brandGold: "#8FA3B8",
  noteAccent: "#E2B94E",

  kpiAccents: ["#3B9B63", "#4FB878", "#8FA3B8", "#6B7A8D"],
  kindAccents: { leasehold: "#82ABD6", mineral: "#D9964F" },
  wellsAccent: "#8FA3B8",

  bgPage: "#111820",
  bgSurface: "#1A2332",
  bgSurface2: "#232F3E",
  bgSidebar: "#1A2332",
  bgHeader: "#1A2332",

  borderColor: "rgba(255,255,255,0.12)",
  borderSubtle: "rgba(255,255,255,0.06)",

  textPrimary: "rgba(255,255,255,0.9)",
  textMuted: "rgba(255,255,255,0.5)",
  textDim: "rgba(255,255,255,0.3)",
  textOnBrand: "#FFFFFF",

  sidebarActiveBg: "rgba(46,125,79,0.18)",
  sidebarActiveBorder: "#3B9B63",
  sidebarActiveText: "#FFFFFF",
  sidebarInactiveText: "rgba(255,255,255,0.55)",
}

// Client-selectable themes. Dark variants are not listed here — they are
// served automatically via `darkThemeFor` when the user flips the mode toggle.
export const allThemes: ClientTheme[] = [sowleTheme, energyQuestTheme, landtrackTheme]

// clientId -> dark counterpart. The mode toggle only appears for themes with
// an entry here; other themes always render their base (light or dark) palette.
export const darkThemeFor: Record<string, ClientTheme> = {
  landtrack: landtrackDarkTheme,
}
